<template>
  <div class="card occupancy-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ $t('dashboard.occupancy.title') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.occupancyCard')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h3>
      <p class="subtitle">{{ $t('dashboard.occupancy.subtitle') }}</p>
    </div>
    <div class="card-body">
      <div class="donut-chart" @click="showDetailModal">
        <canvas ref="occupancyGaugeCanvas"></canvas>
      </div>
    </div>

    <!-- Detail Modal -->
    <div class="modal fade" id="occupancyDetailModal" tabindex="-1" aria-labelledby="occupancyDetailModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="occupancyDetailModalLabel">{{ $t('dashboard.occupancy.title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.occupancy.occupancy_rate') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ occupancyRate.toFixed(1) }}%</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.occupancy.total_rooms') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ totalRooms.toLocaleString() }}{{ $t('dashboard.occupancy.rooms_unit') }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.occupancy.booked_rooms') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ bookedRooms.toLocaleString() }}{{ $t('dashboard.occupancy.rooms_unit') }}</span>
            </div>
            <div class="detail-row">
              <span class="additional-note">{{ $t('dashboard.occupancy.additional_note') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { Modal } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const { t: $t } = useI18n();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true
  }
});

const occupancyGaugeCanvas = ref(null);
const occupancyRate = ref(0);
const totalRooms = ref(0);
const bookedRooms = ref(0);
const loading = ref(false);
let chartInstance = null;

// Modal instance
let modalInstance = null;

// Show detail modal
const showDetailModal = () => {
  if (!modalInstance) {
    const modalElement = document.getElementById('occupancyDetailModal');
    if (modalElement) {
      modalInstance = new Modal(modalElement);
    }
  }
  modalInstance?.show();
};

// Fetch statistics data
const fetchStatistics = async () => {
  if (!props.dateRange || !props.dateRange.startDate || !props.dateRange.endDate) {
    return;
  }

  loading.value = true;
  try {
    const startDate = formatDate(props.dateRange.startDate);
    const endDate = formatDate(props.dateRange.endDate);

    const response = await axios.get('/api/user/statistics/dashboard/accommodation-occupancy', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });

    if (response.data) {
      occupancyRate.value = response.data.occupancy_rate || 0;
      totalRooms.value = response.data.total_rooms || 0;
      bookedRooms.value = response.data.booked_rooms || 0;
    } else {
      occupancyRate.value = 0;
      totalRooms.value = 0;
      bookedRooms.value = 0;
    }

    // Update chart
    updateChart();
  } catch (error) {
    console.error('Error fetching accommodation occupancy statistics:', error);
    occupancyRate.value = 0;
    totalRooms.value = 0;
    bookedRooms.value = 0;
    updateChart();
  } finally {
    loading.value = false;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchStatistics();
}, { deep: true });

onMounted(() => {
  nextTick(() => {
    initializeOccupancyGauge();
    fetchStatistics();
  });
});

const initializeOccupancyGauge = () => {
  if (!occupancyGaugeCanvas.value) return;

  const ctx = occupancyGaugeCanvas.value.getContext('2d');

  // Pre-translate labels for use in Chart.js callback
  const bookedLabel = $t('dashboard.occupancy.booked_rooms_label');
  const availableLabel = $t('dashboard.occupancy.available_rooms_label');

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      datasets: [{
        data: [occupancyRate.value, 100 - occupancyRate.value],
        backgroundColor: [colors.blue[500], colors.gray[100]],
        borderWidth: 0,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const labels = [bookedLabel, availableLabel];
              return labels[context.dataIndex] + ': ' + context.parsed.toFixed(1) + '%';
            }
          }
        }
      }
    },
    plugins: [{
      id: 'centerText',
      afterDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();
        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;

        // Calculate responsive font size based on chart height
        const chartHeight = chart.chartArea.bottom - chart.chartArea.top;
        const fontSize = Math.max(Math.floor(chartHeight / 6), 16);

        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.fillStyle = colors.textPrimary;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(occupancyRate.value + '%', centerX, centerY);
        ctx.restore();
      }
    }]
  });
};

// Update chart with new data
const updateChart = () => {
  if (chartInstance) {
    chartInstance.data.datasets[0].data = [occupancyRate.value, 100 - occupancyRate.value];
    chartInstance.update();
  }
};
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.card-header {
  background-color: v-bind('colors.white');
  border: none;
  padding: 0;
  margin-bottom: 16px;
}

.card-body {
  padding: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0;

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }
}

.occupancy-card {
  .subtitle {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: v-bind('colors.gray[400]');
    margin-top: 4px;
    margin-bottom: 0;
  }

  .donut-chart {
    height: 200px;
    width: 200px;
    max-width: 100%;
    margin: 0 auto;
    cursor: pointer;

    @media (max-width: 768px) {
      height: 250px;
      width: 250px;
      max-width: 100%;
    }
  }
}

// Modal styles
.modal-content {
  .modal-header {
    background-color: v-bind('colors.white');
    border-bottom: 1px solid v-bind('colors.gray[200]');

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .modal-body {
    padding: 24px;

    .detail-row {
      display: grid;
      grid-template-columns: 1fr auto 3fr;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid v-bind('colors.gray[100]');
      gap: 8px;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: left;
        white-space: nowrap;
      }

      .detail-unit {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }

      .detail-value {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: right;
      }

      .additional-note {
        font-size: 12px;
        font-weight: 400;
        white-space: normal;
        color: v-bind('colors.textLight');
        word-wrap: break-word;
        overflow-wrap: break-word;
        grid-column: 1 / -1;
      }
    }
  }
}
</style>
